import { useState } from 'react'
import { CalendarDays, CalendarPlus } from 'lucide-react'
import { toast } from 'sonner'
import { useUpdateProject } from '@/api/hooks'
import { LoadingButton } from '@/components/loading-button'
import { Input } from '@/components/ui/input'
import { ActionPopover } from '@/components/action-popover'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { formatApiErrorMessage } from '@/lib/api-error'
import { shortDate } from '@/lib/delivery-timing'
import { cn } from '@/lib/utils'

/**
 * When the customer needs the project by; click it to add, change or remove the date (USA-1475). It is the
 * same date a dropship quote's "Deliver by" sets, so every PO's timeline and delivery warnings follow it.
 * Safe inside row links: clicks never reach the row.
 */
export function ProjectNeedByPicker({
  projectUuid,
  needBy,
  align = 'start',
  className,
}: {
  projectUuid: string
  needBy?: string | null
  align?: 'start' | 'center' | 'end'
  className?: string
}) {
  const update = useUpdateProject(projectUuid)
  const saved = needBy ?? ''
  // The date just saved, shown until the refreshed project arrives (``base`` is what it said then).
  const [picked, setPicked] = useState<{ value: string; base: string } | null>(null)
  const shown = picked && picked.base === saved ? picked.value : saved
  const [open, setOpen] = useState(false)
  // On a phone the date picker opens on a tap, not as soon as the sheet does.
  const phone = useMediaQuery(PHONE_QUERY)
  const [draft, setDraft] = useState(shown)

  const save = (value: string) => {
    if (value === shown) {
      setOpen(false)
      return
    }
    setPicked({ value, base: saved })
    update.mutate(
      { need_by: value || null },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => {
          setPicked(null)
          toast.error(formatApiErrorMessage('Deliver-by date not saved', error))
        },
      },
    )
  }
  const Icon = shown ? CalendarDays : CalendarPlus
  const label = shown ? `Deliver by ${shortDate(shown)}. Change date` : 'Add deliver-by date'

  // The shared quick-action popover (USA-1536): a bottom sheet on phones, where the date picker
  // opens on a tap rather than as soon as the sheet does.
  return (
    <ActionPopover
      isolate
      align={align}
      open={open}
      onOpenChange={(next) => {
        if (update.isPending) return
        if (next) setDraft(shown)
        setOpen(next)
      }}
      title="Customer needs it by"
      confirmLabel="Save"
      canConfirm={Boolean(draft) && draft !== shown}
      pending={update.isPending && update.variables?.need_by !== null}
      pendingLabel="Saving..."
      onConfirm={() => save(draft)}
      secondaryAction={
        shown ? (
          <LoadingButton
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 text-muted-foreground"
            loading={update.isPending && update.variables?.need_by === null}
            onClick={() => save('')}
          >
            Remove
          </LoadingButton>
        ) : undefined
      }
      trigger={
        <button
          type="button"
          aria-label={label}
          title={label}
          aria-busy={update.isPending}
          className={cn(
            'inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 -mx-1.5 transition-[background-color,box-shadow] hover:bg-muted hover:ring-2 hover:ring-ring/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=open]:ring-2 data-[state=open]:ring-ring/40',
            className,
          )}
        >
          <Icon className="size-3.5 text-muted-foreground" aria-hidden />
          {shown ? (
            <span>
              <span className="text-muted-foreground">Deliver by </span>
              <span className="font-medium tabular-nums">{shortDate(shown)}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Add deliver-by date</span>
          )}
        </button>
      }
    >
      <Input
        type="date"
        aria-label="Customer needs it by"
        autoFocus={!phone}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className="h-9"
      />
    </ActionPopover>
  )
}
